<!-- Split Screen Player Component -->
<div class="split-screen-container"
  #playerContainer
  [class.fullscreen]="isFullscreen"
  [class.mobile]="isMobile"
  [class.in-modal]="isModal">
  <!-- Header Bar -->
  <app-course-player-header
    [title]="moduleTitle"
    [customHeaderContent]="customHeaderContent"
    [currentIndex]="currentItemIndex"
    [totalItems]="totalItems"
    [canGoToPrevious]="canGoToPrevious"
    [canGoToNext]="canGoToNext"
    [showNavigation]="isModal && showNavigation"
    [showTimelineToggle]="showTimelineToggle"
    [timelineVisible]="timelineVisible"
    [showFullscreen]="allowFullscreen && fullscreenAvailable"
    [isFullscreen]="isFullscreen"
    [showCloseButton]="isModal && showCloseButton"
    [mode]="mode"
    (onNavigatePrevious)="navigatePrevious()"
    (onNavigateNext)="navigateNext()"
    (onTimelineToggle)="onTimelineToggle.emit()"
    (onFullscreenToggle)="toggleFullscreen()"
    (onClose)="closePlayer()">
  </app-course-player-header>

  <!-- Debug: Module/Unit ID overlay -->
  @if (debug.isEnabled('moduleUnitIds')) {
    <div class="flex flex-wrap gap-1 text-xs font-mono px-3 py-1 bg-base-200" data-testid="split-screen-debug-ids">
      @if (moduleId) {
        <span class="badge badge-ghost badge-xs">MID: {{ moduleId | slice:0:8 }}...</span>
      }
      @if (unitId) {
        <span class="badge badge-ghost badge-xs">UID: {{ unitId | slice:0:8 }}...</span>
      }
      @if (courseId) {
        <span class="badge badge-ghost badge-xs">CID: {{ courseId | slice:0:8 }}...</span>
      }
    </div>
  }

  <!-- Main Content Area -->
  <div class="content-area" #contentArea [class.stacked]="isMobile">
    <!-- Video Panel (hidden for standalone quizzes with no video) -->
    @if (hasVideo) {
      <div class="video-panel" [style.width.%]="isMobile ? 100 : splitPosition" role="region" aria-label="Video player" data-testid="split-screen-player-video-panel">
        <div class="video-wrapper">
          <!-- Loading state for secure video -->
          @if (isLoadingVideo) {
            <div class="absolute inset-0 flex items-center justify-center bg-base-300" data-testid="split-screen-player-video-loading">
              <div class="text-center text-base-content">
                <span class="loading loading-spinner loading-lg mb-2"></span>
                <p class="text-sm">Loading secure video...</p>
              </div>
            </div>
          }

          <!-- Video Player.
               KAN-306 post-QA: the in-player chip is fed from the SAME hydrated
               pair as the status-bar chip below (displayedWatchPercentage /
               displayedWatchCompleted) and gated on the same !progressLoading
               condition. Those three bindings move together on purpose: a
               constant [showWatchProgress]="true" plus a percentage that could
               only arrive once the video reported a duration is what let the
               chip paint "✓ Watched · 0%" for ~300ms on a module that already
               had progress. -->
          @if (!isLoadingVideo && secureVideoUrl) {
            <app-bunny-player
              [videoUrl]="secureVideoUrl"
              [cdnHostname]="cdnHostname"
              [startPosition]="lastTrackedTime || 0"
              [maxWatchedPosition]="cumulativeWatchTime || 0"
              [restrictForwardSeek]="isVideoRequired"
              [debugVideoName]="moduleTitle"
              [showWatchProgress]="!progressLoading"
              [watchedPercentage]="displayedWatchPercentage"
              [watchCompleted]="displayedWatchCompleted"
              (videoEnded)="onVideoEnded()"
              (timeUpdate)="onVideoProgress($event)"
              (videoReady)="onVideoReady($event)"
              (videoLoaded)="onBunnyVideoLoaded($event)"
              class="w-full h-full">
            </app-bunny-player>
          }
        </div>

        <!-- Video Progress Status Bar -->
        @if (videoWatchPercentage > 0 || quizSubmitted || (moduleProgress && (moduleProgress.videoWatchPercentage > 0 || moduleProgress.quizScore !== null))) {
          <div class="video-status-bar p-3" data-testid="split-screen-player-status-bar">
            <div class="flex items-center gap-2 flex-wrap">
              <!-- Watch progress: ONE chip for ONE metric (KAN-306). This
                   replaced two mutually exclusive badges ("Video Complete - N%"
                   and "Video Progress: N%") that rendered the same number under
                   two labels, while the player rendered it under a third.
                   PHASE 3 FIX retained: only show once progress has loaded, so a
                   remount never flashes "Not started" over real progress. -->
              @if (!progressLoading && (displayedWatchCompleted || displayedWatchPercentage > 0)) {
                <app-watch-progress-chip
                  size="sm"
                  [percent]="displayedWatchPercentage"
                  [completed]="displayedWatchCompleted"></app-watch-progress-chip>
              }
              <!-- Quiz Score Badge (from current session) -->
              @if (quizSubmitted && !isPartiallyGraded) {
                <div
                  class="badge badge-secondary gap-1">
                  <app-icon name="quiz" size="xs"></app-icon>
                  Quiz: {{ autoGradedScore | scorePts:autoGradedMaxScore }}
                  <!-- KAN-127 Phase 2: add percentage from formatScoreParts (anti-contradiction clamp) -->
                  @if (justSubmittedScoreParts) {
                    <span data-testid="status-bar-quiz-percentage">({{ justSubmittedScoreParts.percentText }})</span>
                  } @else {
                    <span>({{ quizPassed ? 'Passed' : 'Failed' }})</span>
                  }
                </div>
              }
              <!-- Pending Review badge (outcome uncertain) -->
              @if (quizSubmitted && isPartiallyGraded && !guaranteedFail) {
                <div class="badge badge-warning gap-1">
                  <app-icon name="pending" size="xs"></app-icon>
                  Pending Review
                </div>
              }
              <!-- Guaranteed fail badge (shows score) -->
              @if (quizSubmitted && isPartiallyGraded && guaranteedFail) {
                <div class="badge badge-error gap-1">
                  <app-icon name="quiz" size="xs"></app-icon>
                  Quiz: {{ autoGradedScore | scorePts:autoGradedMaxScore }} (Failed)
                </div>
              }
              <!-- Quiz Score Badge (from saved progress) -->
              <!-- KAN-627: prior completion credit is credit, not an earned score -->
              @if (!quizSubmitted && moduleProgress?.quizPriorCredit === true) {
                <div class="badge badge-success gap-1 whitespace-nowrap" data-testid="status-bar-prior-credit">
                  <app-icon name="quiz" size="xs"></app-icon>
                  Quiz: Credited
                </div>
              } @else if (!quizSubmitted && moduleProgress && moduleProgress.quizScore !== null && moduleProgress.quizScore !== undefined) {
                <div
                  class="badge badge-secondary gap-1 whitespace-nowrap">
                  <app-icon name="quiz" size="xs"></app-icon>
                  @if (shouldShowPoints(moduleProgress)) {
                    Quiz: {{ getEarnedPoints(moduleProgress) | scorePts:getTotalPoints(moduleProgress) }}
                  } @else {
                    <!-- KAN-127 follow-up: route the percentage-only branch through the
                         clamp too (was raw `number:'1.0-2'`, bypassing the clamp). -->
                    @if (savedProgressScoreParts(moduleProgress); as pctParts) {
                      Quiz: {{ pctParts.percentText }}
                    } @else {
                      Quiz: {{ moduleProgress.quizScore | number:'1.0-0' }}%
                    }
                  }
                  <!-- KAN-127 Phase 2: add percentage from formatScoreParts for saved-progress badge -->
                  @if (savedProgressScoreParts(moduleProgress); as parts) {
                    <span data-testid="status-bar-quiz-percentage">({{ parts.percentText }})</span>
                  } @else {
                    <span>({{ isModuleProgressPassed(moduleProgress) ? 'Passed' : 'Failed' }})</span>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>
    }

    <!-- Resizable Divider (Desktop Only, only when video exists) -->
    @if (!isMobile && hasVideo) {
      <div
        class="divider-handle"
        (mousedown)="startDrag($event)"
        (touchstart)="startDrag($event)"
        (click)="$event.preventDefault(); $event.stopPropagation()"
        #divider
        role="separator"
        aria-label="Resize video and quiz panels">
        <div class="divider-line"></div>
        <div class="divider-grip">
          <app-icon name="drag_indicator" size="sm"></app-icon>
        </div>
      </div>
    }

    <!-- Quiz Panel (full-width when no video) -->
    <div class="quiz-panel" [style.width.%]="isMobile ? 100 : (hasVideo ? (100 - splitPosition) : 100)" role="region" aria-label="Quiz panel" data-testid="split-screen-player-quiz-panel">
      <app-quiz-panel
        [quizQuestions]="quizQuestions"
        [quizDescription]="quizDescription"
        [passingScore]="passingScore"
        [quizId]="quizId"
        [moduleId]="moduleId"
        [courseId]="courseId"
        [isVideoRequired]="isVideoRequired"
        [videoCompleted]="videoCompleted"
        [videoCompletedFromBackend]="videoCompletedFromBackend"
        [videoWatchPercentage]="videoWatchPercentage"
        [requiredWatchPercentage]="requiredWatchPercentage"
        [videoEnded]="videoEnded"
        [videoId]="videoId"
        [videoUrl]="videoUrl"
        [mode]="mode"
        [currentPlayerConfig]="currentPlayerConfig"
        [effectiveVideoRequirement]="effectiveVideoRequirement"
        [itemType]="itemType"
        [isModal]="isModal"
        [canGoToPrevious]="canGoToPrevious"
        [canGoToNext]="canGoToNext"
        [isLastItem]="isLastItem"
        [completionPending]="completionPending"
        [moduleProgress]="moduleProgress"
        [useServerScoring]="useServerScoring"
        [quizSubmissionError]="quizSubmissionError"
        [quizSubmissionErrorInfo]="quizSubmissionErrorInfo"
        [feedbackVisibility]="feedbackVisibility"
        [feedbackFlags]="feedbackFlags"
        [revealAnswersOnlyOnPass]="revealAnswersOnlyOnPass"
        [customIncorrectMessage]="customIncorrectMessage"
        [customPassMessage]="customPassMessage"
        (quizSubmit)="onQuizSubmitted($event)"
        (quizRetry)="onQuizRetried()"
        (submissionRestored)="onSubmissionRestored($event)"
        (completeModule)="completeModule()"
        (navigateNext)="navigateNext()"
        (navigatePrevious)="navigatePrevious()"
        (closePlayer)="closePlayer()"
        (progressUpdate)="onProgressUpdate.emit($event)">
      </app-quiz-panel>
    </div>
  </div>
</div>